import { FC } from "react";
import styled from "styled-components";
import colors from "../../styles/colors";

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    // padding: 1rem 0;
    // margin: 1rem 0;
    flex-wrap: wrap;
`;

interface PaginationButtonProps {
    disabled?: boolean;
}

const PaginationButton = styled.button<PaginationButtonProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 40px;
    padding: 0 12px;
    border: 2px solid rgba(255, 255, 255, 0.15);
    background: ${props => props.disabled ? 'rgba(255,255,255,0.05)' : colors.bgColorBlack2};
    color: ${props => props.disabled ? 'rgba(255,255,255,0.25)' : 'white'};
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.1s ease-in-out;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);

    &:hover:not(:disabled) {
        background: ${colors.bgColorBordeaux};
        color: white;
        border-color: ${colors.bgColorBordeaux};
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(163, 27, 27, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 4px rgba(163, 27, 27, 0.3);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const CurrentPageIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
    height: 40px;
    padding: 0 16px;
    background: ${colors.bgColorBordeaux};
    color: gold;
    border-radius: 8px;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(163, 27, 27, 0.4);
    border: 2px solid gold;
`;

const PageInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    font-weight: 500;
    margin: 0 1rem;

    @media (max-width: 768px) {
        margin: 0.5rem 0;
        width: 100%;
        justify-content: center;
    }
`;

const PaginationGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    @media (max-width: 768px) {
        flex-wrap: wrap;
        justify-content: center;
    }
`;

interface BoxPageProps {
    maxPageApi: number;
    currentPage: number;
    onPageChange: (pageNumber: number) => void;
    className?: string;
}

const BoxPage: FC<BoxPageProps> = (props) => {
    const { maxPageApi, currentPage, onPageChange, className } = props;

    const handlePage = (pageNumber: number): void => {
        if (pageNumber >= 1 && pageNumber <= maxPageApi) {
            onPageChange(pageNumber);
        }
    };

    const goToFirstPage = (): void => handlePage(1);
    const goToLastPage = (): void => handlePage(maxPageApi);
    const goToPreviousPage = (): void => handlePage(currentPage - 1);
    const goToNextPage = (): void => handlePage(currentPage + 1);

    return (
        <PaginationContainer className={className}>
            <PaginationGroup>
                {/* Tasto Prima Pagina */}
                <PaginationButton
                    disabled={currentPage === 1}
                    onClick={goToFirstPage}
                    title="Prima pagina"
                >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 9H17a1 1 0 110 2h-5.586l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M6.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L2.414 9H8a1 1 0 110 2H2.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                </PaginationButton>

                {/* Tasto Pagina Precedente */}
                <PaginationButton
                    disabled={currentPage === 1}
                    onClick={goToPreviousPage}
                    title="Pagina precedente"
                >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </PaginationButton>
            </PaginationGroup>

            {/* Indicatore Pagina Corrente */}
            <CurrentPageIndicator>
                {currentPage}
            </CurrentPageIndicator>

            {/* Informazioni Pagine */}
            <PageInfo>
                di {maxPageApi}
            </PageInfo>

            <PaginationGroup>
                {/* Tasto Pagina Successiva */}
                <PaginationButton
                    disabled={currentPage === maxPageApi}
                    onClick={goToNextPage}
                    title="Pagina successiva"
                >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                </PaginationButton>

                {/* Tasto Ultima Pagina */}
                <PaginationButton
                    disabled={currentPage === maxPageApi}
                    onClick={goToLastPage}
                    title="Ultima pagina"
                >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L8.586 11H3a1 1 0 110-2h5.586L4.293 4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M13.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L17.586 11H12a1 1 0 110-2h5.586L13.293 4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </PaginationButton>
            </PaginationGroup>
        </PaginationContainer>
    );
};

export default BoxPage;
